import { AfterViewInit, Component, ElementRef, EventEmitter, input, Output, viewChild } from '@angular/core';

import { NameInLegendOption } from 'ChartApp/ts/enums/chart-common.enum';
import { DataInLegendOption, FundChartOptionValue, ValueFlagOption } from 'ChartApp/ts/enums/fund-chart-common.enum';
import { Frequency } from 'ChartApp/ts/enums/tech-chart-common.enum';
import { MultiSelectDropdownConfig } from 'Angular/features/components/dropdowns/multi-select-dropdown/interfaces/multi-select-dropdown-config.interface';
import { MultiSelectDropdownItem } from 'Angular/features/components/dropdowns/multi-select-dropdown/interfaces/multi-select-dropdown-item.interface';
import { SingleSelectDropdownConfig } from 'Angular/features/components/dropdowns/single-select-dropdown/interfaces/single-select-dropdown-config.interface';
import { SingleSelectDropdownItem } from 'Angular/features/components/dropdowns/single-select-dropdown/interfaces/single-select-dropdown-item.interface';

import { DropdownHelperService } from 'Angular/services/dropdown-helper.service';

/**
 * Chart Options Component
 *
 * This component shows a list of chart options and checkboxes in dropdown. The checkboxes show whether
 * the corresponding item is currently selected. Below that, there is a nested single-select dropdown
 *  with options for how the name is displayed in the legend.
 *
 * Input Properties:
 *      chartOptionsConfig: Config for the main chart options dropdown
 *      nameInLegendOptionsConfig: Config for the nested "name in legend" options dropdown
 *      dataInLegendDropdownConfig: Config for the nested "data in legend" options dropdown
 *      frequencyOptionsConfig: Config for the nested "frequency" options dropdown
 *
 * Output Properties:
 *      closeHandler: EventEmitter that emits whenever the dropdown is closed
 *      chartOptionChangeHandler: EventEmitter that emits whenever a chart option is selected/deselected
 *      dataInLegendChangeHandler: emits when the selected "data in legend" option is changed
 *      nameInLegendChangeHandler: EventEmitter that emits whenever a "name in legend" option is changed
 *      frequencyChangeHandler: EventEmitter that emits whenever a "frequency" option is changed
 *
 * Internal Properties:
 *      isOpen: boolean for whether the dropdown is displaying the selectable items
 */
@Component({ standalone: false, selector: 'ycn-chart-options', templateUrl: './chart-options.template.html' })
export class ChartOptionsComponent implements AfterViewInit {
    readonly chartOptionsConfig = input.required<MultiSelectDropdownConfig>();

    readonly nameInLegendOptionsConfig = input.required<SingleSelectDropdownConfig>();

    readonly dataInFooterDropdownConfig = input<MultiSelectDropdownConfig>();

    readonly dataInLegendDropdownConfig = input<SingleSelectDropdownConfig>();

    readonly valueFlagsDropdownConfig = input<SingleSelectDropdownConfig>();

    readonly currencyDropdownConfig = input<SingleSelectDropdownConfig>();

    readonly frequencyOptionsConfig = input<SingleSelectDropdownConfig>();

    @Output() chartOptionChangeHandler = new EventEmitter<MultiSelectDropdownItem>();

    @Output() nameInLegendChangeHandler = new EventEmitter<NameInLegendOption>();

    @Output() dataInLegendChangeHandler = new EventEmitter<DataInLegendOption>();

    @Output() valueFlagsChangeHandler = new EventEmitter<ValueFlagOption>();

    @Output() currencyChangeHandler = new EventEmitter<string>();

    @Output() frequencyChangeHandler = new EventEmitter<Frequency>();

    @Output() closeHandler = new EventEmitter<void>();

    readonly el = viewChild.required<ElementRef>('chartOptions');

    isOpen: boolean = false;

    isCurrencyMenuOpen: boolean = false;

    readonly FundChartOptionValue = FundChartOptionValue;

    constructor(private dropdownHelperService: DropdownHelperService) {}

    ngAfterViewInit(): void {
        const autoCloseHandler = (): void => {
            if (this.isOpen) {
                this.toggle();
            }
        };
        this.dropdownHelperService.setCloseHandler(this.el().nativeElement, autoCloseHandler);
    }

    toggle(): void {
        if (this.isOpen) {
            this.isOpen = false;
            this.closeHandler.emit();
        } else {
            this.isOpen = true;
        }
    }

    updateChartOptions(newOption: MultiSelectDropdownItem): void {
        this.chartOptionChangeHandler.emit(newOption);
    }

    updateChartNameInLegend(newOption: NameInLegendOption): void {
        this.nameInLegendChangeHandler.emit(newOption);
    }

    updateChartFrequency(newOption: Frequency): void {
        this.frequencyChangeHandler.emit(newOption);
    }

    updateChartDataInLegend(newOption: DataInLegendOption): void {
        this.dataInLegendChangeHandler.emit(newOption);
    }

    updateChartValueFlags(newOption: ValueFlagOption): void {
        this.valueFlagsChangeHandler.emit(newOption);
    }

    updateChartCurrency(newOption: SingleSelectDropdownItem): void {
        // A currency only shows as selected while conversion is on.
        // Picking any unselected currency (including the saved currency while conversion is off) enables conversion.
        // No-ops when re-picking the same selected currency while currency conversion is on.
        if (newOption.type === 'divider' || !newOption.value || newOption.selected) {
            return;
        }
        this.currencyChangeHandler.emit(newOption.value);
    }
}
